import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

import useTableInstance from "hooks/useTableInstance";
import useDataSourceManager from "hooks/useDataSourceManager";

import { v4 as uuidv4 } from "uuid";
import { createColumnHelper } from '@tanstack/react-table';

import { useTheme } from "@mui/material/styles";
import { Box, Button, Typography, CircularProgress, Stack, Divider, IconButton } from "@mui/material";

import ModalContainer from "components/ModalContainer";
import TableField from "components/Table";
import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import { SolidWarnButton } from "components/buttons/SolidWarnButton";
import { MenuSelect } from "components/Select/menuSelect";

import CaretRightFilled from "@ant-design/icons/CaretRightFilled";
import WarningFilled from "@ant-design/icons/WarningFilled";
import CloseOutlined from "@ant-design/icons/CloseOutlined";

import { DEVICE_ICONS } from "const"
import {
  BATCH_ACTION_MODAL_TITLE,
  BATCH_ACTION_MODAL_DESCRIPTION,
  BATCH_ACTION_MODAL_BUTTON_NAME
} from "../const"

import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

const BatchActionModal = ({
  tabVal,
  currentModal,
  display,
  handleCloseModal,
  auth,
  currentTableSelected,
  clickSiteInventory,
  fetchTableData,
  settingsData
}) => {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [isInventory, setIsInventory] = useState(false);
  const [sitesUuid, _setSitesUuid] = useState(uuidv4());
  const [orgMoveSitesUuid, _orgMoveSetSitesUuid] = useState(uuidv4());
  const [siteMoveSitesUuid, _siteMoveSetSitesUuid] = useState(uuidv4());
  const [orgRemoveFromSiteUuid, _orgRemoveFromSiteUuid] = useState(uuidv4());
  const [orgDeleteDeviceUuid, _orgDeleteDeviceUuid] = useState(uuidv4());

  const [sitesData, setSitesData] = useState([]);
  const [selectedMoveSite, setSelectedMoveSite] = useState(null);
  const [showConfirmServiceChangeModal, setShowConfirmServiceChangeModal] = useState(false);

  const [preConfirmSocketId, setPreConfirmSocketId] = useState(null);
  const [preConfirmResult, setPreConfirmResult] = useState(null);
  const [isPreConfirmLoading, setIsPreConfirmLoading] = useState(false);
  const [activePreConfirmData, setActivePreConfirmData] = useState(null);

  useEffect(() => {
    if (display && currentTableSelected.length > 0) {
      setSelectedData((pre) => [
        ...pre,
        ...currentTableSelected.map((item) => item.original),
      ]);
      setIsInventory((!tabVal || tabVal.indexOf('inventory') > -1) || clickSiteInventory);
    }

    return () => {
      setSelectedData([]);
      setSitesData([]);
      setSelectedMoveSite(null);
      setShowConfirmServiceChangeModal(false);
    };
  }, [display]);

  const columnHelper = createColumnHelper();

  const columnsData = [
    columnHelper.accessor('deviceName', {
      header: 'Device Name',
      cell: info => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center" }}>
          <Box>
            {
              DEVICE_ICONS[info.row.original.deviceType][info.row.original.linkStatus] &&
              (
                <img
                  src={DEVICE_ICONS[info.row.original.deviceType][info.row.original.linkStatus]}
                  alt={`${info.row.original.deviceType}_${info.row.original.linkStatus}`}
                />
              )
            }
          </Box>
          <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginLeft: '10px' }}>
            {info.getValue()}
          </Box>
        </Box>
      )
    }),
    columnHelper.accessor('siteName', {
      header: 'Site',
    }),
    columnHelper.accessor('macAddress', {
      header: 'MAC Address',
    }),
    columnHelper.accessor('modelName', {
      header: 'Model Name',
    }),
    columnHelper.accessor('localIpAddr', {
      header: 'Local Mgmt. IP',
    }),]

  const table = useTableInstance({
    tableName: "batchAction",
    data: selectedData,
    columns: columnsData,
  });

  const { dataMap, getData, sendRequest } = useDataSourceManager();

  useEffect(() => {
    if (currentModal === 'moveToSite') {
      if (Object.keys(auth.orgs).length === 0) {
        sendRequest('/orgs/:orgId/sites.read', sitesUuid, { orgId: auth.realm.id })
      } else {
        sendRequest('/ents/:entId/orgs/:orgId/sites.read', sitesUuid, { entId: auth.realm.id, orgId: auth.orgs.id })
      }
    }
  }, [currentModal])

  useEffect(() => {
    const sitesResponse = getData(sitesUuid);
    if (sitesResponse) {
      setSitesData(sitesResponse.data.sites)
    }

    // Close modal and refresh table after action success
    const orgMoveSiteResponse = getData(orgMoveSitesUuid);
    const siteMoveSiteResponse = getData(siteMoveSitesUuid);
    const removeFromSiteResponse = getData(orgRemoveFromSiteUuid);
    const deleteDeviceResponse = getData(orgDeleteDeviceUuid);

    if (orgMoveSiteResponse || siteMoveSiteResponse || removeFromSiteResponse || deleteDeviceResponse) {
      setLoading(false);
      handleCloseModal();

      if (clickSiteInventory) {
        fetchTableData(true);
        return;
      }

      fetchTableData();
    }
  }, [dataMap]);

  useEffect(() => {
    if (preConfirmSocketId && dataMap[preConfirmSocketId]) {
      const response = getData(preConfirmSocketId);
      if (response) {
        console.log("Pre-confirm response:", response);
        setPreConfirmResult(response);
        setIsPreConfirmLoading(false);
      }
    }
  }, [dataMap, preConfirmSocketId]);

  useEffect(() => {
    if (preConfirmResult) {
      const data = preConfirmResult.data || [];
      const hasSubscribedService = data.some(item => item.service && item.service.length > 0);

      setActivePreConfirmData(data);
      setPreConfirmResult(null);

      if (hasSubscribedService) {
        setShowConfirmServiceChangeModal(true);
      } else {
        handleSubmit();
      }
    }
  }, [preConfirmResult]);

  const sendPreConfirmRequest = () => {
    const isUnityRd3 = window.location.hostname.includes('unity-rd3');
    if (!isUnityRd3) return;

    setIsPreConfirmLoading(true);
    setPreConfirmResult(null);
    const newSocketId = uuidv4();
    setPreConfirmSocketId(newSocketId);

    const standalone = Object.keys(auth.orgs).length === 0;
    const apiKey = standalone
      ? "/orgs/license/service/subscribe/preConfirm"
      : "/ents/orgs/license/service/subscribe/preConfirm";

    const placeholder = standalone
      ? { orgId: auth.realm.id }
      : { entId: auth.realm.id, orgId: auth.orgs.id };

    const params = [];

    // 1. Source Site (remove)
    const sourceSiteId = selectedData[0]?.siteId || auth.sites?.id;
    if (sourceSiteId && sourceSiteId !== "Inventory") {
      params.push({
        siteId: sourceSiteId,
        action: "remove",
        devices: selectedData.map(d => ({
          modelName: d.modelName,
          deviceId: d.macAddress,
        }))
      });
    }

    // 2. Target Site (add)
    if (selectedMoveSite?.siteId) {
      params.push({
        siteId: selectedMoveSite.siteId,
        action: "add",
        devices: selectedData.map(d => ({
          modelName: d.modelName,
          deviceId: d.macAddress,
        }))
      });
    }

    sendRequest(apiKey, newSocketId, placeholder, params);
  };



  const handleApplyClick = () => {
    const isUnityRd3 = window.location.hostname.includes('unity-rd3');

    if (
      isUnityRd3 &&
      ((currentModal === 'moveToSite' && selectedMoveSite) ||
      currentModal === 'removeFromSite' ||
      currentModal === 'deleteDevice')
    ) {
      sendPreConfirmRequest();
      return;
    }

    handleSubmit();
  };

  function handleSubmit() {
    const standalone = Object.keys(auth.orgs).length === 0;
    const baseUrl = standalone ? `/orgs/:orgId` : `/ents/:entId/orgs/:orgId`;
    let restfulParams = standalone ? {
      orgId: auth.realm.id
    } : {
      entId: auth.realm.id, orgId: auth.orgs.id
    };

    let moveToSiteUrl = `${baseUrl}/devices/assignSite.update`;
    if (isInventory) {
      if (currentModal === 'moveToSite' && auth.scopeLevel === 'site') {
        moveToSiteUrl = `${baseUrl}/sites/:siteId/devices/addFromInventory.update`;
        restfulParams = standalone ? {
          orgId: auth.realm.id, siteId: selectedMoveSite.siteId
        } : {
          entId: auth.realm.id, orgId: auth.orgs.id, siteId: selectedMoveSite.siteId
        };
      }
    }

    const requestConfig = {
      moveToSite: {
        org: {
          url: moveToSiteUrl,
          uuid: orgMoveSitesUuid,
          getRequestParams: () => ({
            siteId: selectedMoveSite.siteId,
            deviceIds: selectedData.map((item) => item.macAddress),
          }),
        },
        site: {
          url: moveToSiteUrl,
          uuid: siteMoveSitesUuid,
          getRequestParams: () => {
            if (isInventory) {
              return selectedData.map((item) => ({
                deviceId: item.macAddress,
              }));
            }

            return {
              siteId: selectedMoveSite.siteId,
              deviceIds: selectedData.map((item) => item.macAddress),
            };
          },
        }
      },
      removeFromSite: {
        org: {
          url: `${baseUrl}/devices/moveToInventory.update`,
          uuid: orgRemoveFromSiteUuid,
          getRequestParams: () => ({
            data: selectedData.map((item) => ({
              deviceId: item.macAddress,
            })),
          }),
        },
        site: {
          url: `${baseUrl}/devices/moveToInventory.update`,
          uuid: orgRemoveFromSiteUuid,
          getRequestParams: () => ({
            data: selectedData.map((item) => ({
              deviceId: item.macAddress,
            })),
          }),
        },
      },
      deleteDevice: {
        org: {
          url: `${baseUrl}/devices/remove.update`,
          uuid: orgDeleteDeviceUuid,
          getRequestParams: () => ({
            data: selectedData.map((item) => ({
              deviceId: item.id,
            })),
          }),
        },
        site: {
          url: `${baseUrl}/devices/remove.update`,
          uuid: orgDeleteDeviceUuid,
          getRequestParams: () => ({
            data: selectedData.map((item) => ({
              deviceId: item.id,
            })),
          }),
        },
      },
    };

    const modalConfig = requestConfig[currentModal]?.[auth.scopeLevel];
    if (modalConfig) {
      const requestParams = modalConfig.getRequestParams();
      sendRequest(modalConfig.url, modalConfig.uuid, restfulParams, requestParams);
    }

    setLoading(true);
  }

  const getIsInsufficient = () => {
    if (activePreConfirmData) {
      const sitesToRender = activePreConfirmData.filter(item => item.service && item.service.length > 0);
      return sitesToRender.some(item => item.sufficientCredits === false);
    }
    return false;
  };
  const isShowLoading = loading || isPreConfirmLoading || selectedData.length === 0;
  const ButtonComponent = currentModal === "deleteDevice" ? SolidWarnButton : SolidButton;
  const pinningOptions = isInventory ? null : [{ value: "Inventory", label: "Inventory", siteId: null }];

  return (
    <ModalContainer display={display} modalWidth={showConfirmServiceChangeModal ? '600px' : '1250px'}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <Typography sx={{
          color: theme.palette.primary.main,
          fontSize: theme.typography.customFontSize[5],
          fontWeight: theme.typography.customFontWeight[3],
        }}>
          {showConfirmServiceChangeModal ? "Confirm Service Change" : BATCH_ACTION_MODAL_TITLE[currentModal]}
        </Typography>
        <Button
          onClick={handleCloseModal}
          disabled={loading}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}>
          <CloseIcon />
        </Button>
      </Box>
      {
        (
          isShowLoading
          && (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <CircularProgress />
            </Box>
          )
        ) ||
        (((currentModal === 'moveToSite' || currentModal === 'removeFromSite' || currentModal === 'deleteDevice') && showConfirmServiceChangeModal) ? (
          // Confirm Service Change UI
          <Box sx={{ position: "relative" }}>
            <Stack spacing={2.5}>
              <Box sx={{ overflowY: "auto", maxHeight: "75vh" }}>
                <Stack spacing={3}>
                  {(() => {
                    if (
                      (currentModal === 'moveToSite' || currentModal === 'removeFromSite' || currentModal === 'deleteDevice') &&
                      activePreConfirmData
                    ) {
                      const serviceNameMapping = {
                        healthAI: "Health AI Insight",
                        nmsPro: "NMS Pro"
                      };
                      const sitesToRender = activePreConfirmData.filter(item => item.service && item.service.length > 0);
                      return (
                        <>
                          {sitesToRender.map((siteItem, idx) => {
                            const serviceText = siteItem.service.map(s => serviceNameMapping[s] || s).join(", ") || "Subscribed Services";
                            const actionText = siteItem.action === "add" ? "Add Device" : siteItem.action === "remove" ? "Remove Device" : siteItem.action;
                            const dailyRateValue = siteItem.dailyRate || 0;
                            const totalCredits = dailyRateValue * (siteItem.remainingDays || 0);

                            return (
                              <Stack key={idx} spacing={2} sx={{ mb: idx < sitesToRender.length - 1 ? 3 : 0 }}>
                                <Stack spacing={1}>
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <CaretRightFilled style={{ fontSize: "14px", color: "#172664" }} />
                                    <Typography sx={{ color: "rgba(0,0,0,0.85)", fontSize: "15px", fontWeight: "bold" }}>
                                      {siteItem.siteName || "Site"}
                                    </Typography>
                                  </Stack>
                                  <Typography sx={{ color: "#172664", fontSize: "16px", fontWeight: "bold", mb: 0.5 }}>
                                    Service: {serviceText}
                                  </Typography>
                                  <Divider sx={{ mb: 1.5 }} />

                                  <Stack spacing={0.5}>
                                    <Stack direction="row" spacing={1}>
                                      <Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>
                                        Action:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: siteItem.action === "remove" ? "#D1003B" : "#008514", fontWeight: "bold" }}>
                                        {actionText}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                      <Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>
                                        Devices:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: siteItem.action === "remove" ? "#D1003B" : "#008514", fontWeight: "bold" }}>
                                        {siteItem.deviceCount}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                      <Typography variant="body2" sx={{ minWidth: "65px", color: "rgba(0,0,0,0.45)" }}>
                                        Rate:
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: "#373737", fontWeight: "bold" }}>
                                        {siteItem.rate || ""}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Stack>

                                <Box sx={{ p: 2, border: "1px solid rgba(0,0,0,0.15)", borderRadius: "12px", mt: 2 }}>
                                  <Typography sx={{ color: "#172664", fontSize: "14px", fontWeight: "bold", mb: 2 }}>
                                    {siteItem.action === "remove" ? "This Month’s Prorated Credit Refund" : "This Month’s Prorated Credit Deduction"}
                                  </Typography>
                                  <Stack spacing={1.5}>
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="body2" color="textSecondary">
                                        Daily Rate
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}>
                                        <Box component="span" sx={{ color: "#008514" }}>
                                          {dailyRateValue.toLocaleString()}
                                        </Box> Credits / day
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="body2" color="textSecondary">
                                        Remaining Days
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}>
                                        <Box component="span" sx={{ color: "#008514" }}>
                                          {siteItem.remainingDays} Days
                                        </Box>
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" justifyContent="space-between">
                                      <Typography variant="body2" color="textSecondary">
                                        Total
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#373737" }}>
                                        <Box component="span" sx={{ color: "#008514" }}>
                                          {totalCredits.toLocaleString()}
                                        </Box> Credits
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Box>
                              </Stack>
                            );
                          })}
                        </>
                      );
                    }
                    return null;
                  })()}

                  {/* Warning Section */}
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, alignItems: "flex-start" }}>
                    <WarningFilled style={{ color: "#D1003B", fontSize: "18px" }} />
                    <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.85)", fontWeight: "bold" }}>
                      {getIsInsufficient()
                        ? "Insufficient credits. Please contact your Organization Super Admin."
                        : "Changes take effect immediately upon confirmation. Credits will be auto-deducted monthly based on your subscription plan."}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>

              {/* Actions 按鈕 */}
              <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 2 }}>
                <OutlineButton
                  onClick={() => setShowConfirmServiceChangeModal(false)}
                  width="120px"
                  height="36px"
                  sx={{ borderRadius: "20px" }}
                >
                  Cancel
                </OutlineButton>
                <SolidButton
                  onClick={() => {
                    setShowConfirmServiceChangeModal(false);
                    handleSubmit();
                  }}
                  disabled={getIsInsufficient()}
                  width="120px"
                  height="36px"
                  sx={{ borderRadius: "20px", bgcolor: "#172664" }}
                >
                  Confirm
                </SolidButton>
              </Stack>
            </Stack>
          </Box>
        ) : (
          // 原本的 Table + Dropdown + Cancel/Move 按鈕畫面
          <>
            <TableField {...table} />
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "25px 0" }}>
              <Typography sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
              }}>
                {BATCH_ACTION_MODAL_DESCRIPTION[currentModal](selectedData[0]?.siteName)}
              </Typography>
            </Box>
            {
              currentModal === 'moveToSite' &&
              <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", margin: "25px 0" }}>
                <Box margin="0 10px">
                  <Typography
                    sx={{
                      fontSize: theme.typography.customFontSize[2],
                      color: theme.palette.primary.black,
                      fontWeight: theme.typography.customFontWeight[1],
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                      Move to Site<Typography sx={{ color: theme.palette.primary.redPrimary }}>*</Typography>
                    </Box>
                  </Typography>
                </Box>
                <Box margin="0 10px">
                  <MenuSelect
                    placeholder="Select Site"
                    value={selectedMoveSite?.value || ""}
                    onChange={(opt) => setSelectedMoveSite(opt)}
                    options={sitesData.map((site) => ({
                      label: site.siteName,
                      value: site.siteId,
                      ...site,
                    }))}
                    pinningOptions={pinningOptions}
                    width="200px"
                    disabled={sitesData.length === 0}
                  />
                </Box>
              </Box>
            }

            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Box margin="0 10px">
                <OutlineButton width="100px" height="32px" onClick={handleCloseModal}>
                  Cancel
                </OutlineButton>
              </Box>
              <Box margin="0 10px">
                <ButtonComponent
                  width="100px"
                  height="32px"
                  onClick={handleApplyClick}
                  disabled={currentModal === 'moveToSite' && !selectedMoveSite}
                >
                  {BATCH_ACTION_MODAL_BUTTON_NAME[currentModal]}
                </ButtonComponent>
              </Box>
            </Box>
          </>
        ))
      }
    </ModalContainer>
  );
};

export default BatchActionModal;